/**
 * @file Especialização Spinner do pipeline Input.
 * @module components/create/spinner
 * @see module:components/create/input
 */

import { applyFullDefault } from './fullDefaults';

/**
 * Configura um input number com controles de incremento/decremento.
 *
 * Modos:
 * - `default`: suffix com setas empilhadas;
 * - `moreless`: botão menos no prefix e mais no suffix;
 * - `leftright`: carets esquerdo/direito;
 * - `none`: nenhum controle adicional.
 *
 * Os callbacks `blur`, `change` e `input` do consumidor são preservados e
 * executados depois da lógica interna correspondente.
 *
 * @param {HTMLInputElement} elem Input base.
 * @param {Object} options Opções recebidas pelo componente.
 * @param {'default'|'moreless'|'leftright'|'none'|string} [options.mode='default'] Controles visuais de incremento/decremento.
 * @param {number} [options.step=1] Incremento do input number.
 * @param {number} [options.min=0] Mínimo, salvo quando `deferMinValidation:true`.
 * @param {number} [options.max=999999] Máximo.
 * @param {number} [options.size] Largura em `ch` quando `full !== true`; calculada a partir de `max` quando ausente.
 * @param {boolean} [options.deferMinValidation=false] Remove `min` durante edição e só o aplica em blur/clamp apropriado.
 * @param {boolean} [options.showPlus=false] Exibe `+` para valores positivos sem alterar o valor numérico do input.
 * @returns {Array.<HTMLInputElement|Object>} Input e o mesmo objeto `options` normalizado.
 * @see module:components/create/input
 */
export default (elem, options) => {
  const showPlus = options.showPlus === true;
  options.fbClass = `${options.fbClass || ''} spinner${showPlus ? ' show-plus' : ''}`.trim();
  applyFullDefault(options);

  const ensureStyleObject = () => {
    if (options.style === undefined) {
      options.style = {};
      return options.style;
    }

    if (typeof options.style === 'string') {
      const style = {};
      const rules = options.style.split(';');
      for (const rule of rules) {
        const [prop, value] = rule.split(':');
        if (prop && value) style[prop.trim()] = value.trim();
      }
      options.style = style;
    }

    return options.style;
  };

  options.type = 'number';
  const deferMinValidation = options.deferMinValidation === true;
  const providedMin = options.min;

  const clearValidity = (num) => {
    if (!num) return;
    num.setCustomValidity('');
    num.parentElement?.parentElement?.classList?.remove('invalid');
  };

  const ensurePlusSign = (num) => {
    if (!showPlus || !num?.parentElement || typeof document === 'undefined') return null;

    let sign = num.parentElement.querySelector(':scope > .spinner-plus-sign');
    if (sign) return sign;

    sign = document.createElement('span');
    sign.className = 'spinner-plus-sign is-hidden';
    sign.setAttribute('aria-hidden', 'true');
    num.before(sign);
    return sign;
  };

  const measureValueWidth = (num) => {
    const raw = String(num?.value ?? '');
    if (!raw) return 0;

    const style = globalThis.getComputedStyle?.(num);
    if (!style || typeof document === 'undefined') return raw.length * 8;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext?.('2d');
    if (!context) return raw.length * 8;

    context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    return context.measureText(raw).width;
  };

  const syncPlusSign = (num) => {
    if (!showPlus || !num) return;
    const sign = ensurePlusSign(num);
    if (!sign) return;

    const value = Number(num.value);
    const visible = num.value !== '' && Number.isFinite(value) && value > 0;
    sign.classList.toggle('is-visible', visible);
    sign.classList.toggle('is-hidden', !visible);
    sign.textContent = visible ? '+' : '';

    if (!visible) {
      sign.style.removeProperty('right');
      return;
    }

    const style = globalThis.getComputedStyle?.(num);
    const paddingRight = Number.parseFloat(style?.paddingRight || '0') || 0;
    const suffix = num.nextElementSibling?.classList?.contains('sfx')
      ? num.nextElementSibling
      : null;
    const suffixWidth = suffix?.getBoundingClientRect?.().width || 0;
    const valueWidth = measureValueWidth(num);
    sign.style.right = `${suffixWidth + paddingRight + valueWidth + 1}px`;
  };

  const clampValue = (num, enforceMin = true) => {
    if (!num) return;
    const min = enforceMin ? Number(providedMin ?? options.min ?? 0) : Number.NEGATIVE_INFINITY;
    const max = Number(options.max ?? Number.POSITIVE_INFINITY);
    let val = Number(num.value);
    if (Number.isNaN(val)) return;
    if (enforceMin && val < min) val = min;
    if (val > max) val = max;
    num.value = val;
    clearValidity(num);
  };

  const emitChange = (num) => {
    if (!num) return;
    num.dispatchEvent(new Event('input', { bubbles: true }));
    num.dispatchEvent(new Event('change', { bubbles: true }));
  };

  if (options.step === undefined) options.step = 1;
  if (!deferMinValidation && options.min === undefined) options.min = 0;
  if (options.max === undefined) options.max = 999999;

  if (options.value === undefined) {
    if (options.defaultValue !== undefined) {
      options.value = options.defaultValue;
    } else {
      options.value = providedMin ?? options.min ?? 0;
    }
  }

  if (deferMinValidation) delete options.min;

  if (options.size === undefined) {
    const maxValue = Number(options.max);
    const maxDigits = Number.isFinite(maxValue) ? String(Math.trunc(Math.abs(maxValue))).length : 0;
    options.size = Math.max(6, maxDigits || 1);
  }

  if (options.full !== true) {
    const style = ensureStyleObject();
    if (style.width === undefined) style.width = `${options.size}ch`;
    if (style.minWidth === undefined) style.minWidth = `${options.size}ch`;
  }

  switch (options.mode) {
    case 'moreless':
      options.prefix = {
        class: 'bt-red spinner-control spinner-control-prefix spinner-control-minus',
        mode: 'soft',
        icon: { type: 'solid', name: 'minus' }, click: (e) => {
          const num = e.currentTarget.parentNode.querySelector('input[type=number]');
          if (num !== null) {
            num.stepDown();
            clampValue(num);
            emitChange(num);
          }
        }
      };
      options.suffix = {
        class: 'bt-green spinner-control spinner-control-suffix spinner-control-plus',
        mode: 'soft',
        icon: { type: 'solid', name: 'plus' }, click: (e) => {
          const num = e.currentTarget.parentNode.querySelector('input[type=number]');
          if (num !== null) {
            num.stepUp();
            clampValue(num);
            emitChange(num);
          }
        }
      };
      break;
    case 'leftright':
      options.prefix = {
        class: 'spinner-control spinner-control-prefix spinner-control-left',
        icon: { type: 'solid', name: 'caret-left' }, click: (e) => {
          const num = e.currentTarget.parentNode.querySelector('input[type=number]');
          if (num !== null) {
            num.stepDown();
            emitChange(num);
          }
        }
      };
      options.suffix = {
        class: 'spinner-control spinner-control-suffix spinner-control-right',
        icon: { type: 'solid', name: 'caret-right' }, click: (e) => {
          const num = e.currentTarget.parentNode.querySelector('input[type=number]');
          if (num !== null) {
            num.stepUp();
            emitChange(num);
          }
        }
      };
      break;
    case 'none':
      break;
    default:
      options.mode = 'default';
      options.suffix = {
        class: 'spinner-buttons',
        html: '<span class="spinner-step" data-spinner-action="up">▲</span><span class="spinner-step" data-spinner-action="down">▼</span>',
        click: (e) => {
          const action = e.target.closest?.('[data-spinner-action]')?.dataset?.spinnerAction;
          if (!action) return;

          const num = e.currentTarget.parentNode?.querySelector('input[type=number]');
          if (num !== null && num !== undefined) {
            if (action === 'up') num.stepUp();
            else if (action === 'down') num.stepDown();
            clampValue(num);
            emitChange(num);
          }
        }
      };
  }

  const userBlur = options.blur;
  options.blur = (e) => {
    clampValue(e.target);
    syncPlusSign(e.target);
    if (typeof userBlur === 'function') userBlur(e);
  };

  const userChange = options.change;
  options.change = (e) => {
    clampValue(e.target, !deferMinValidation);
    clearValidity(e.target);
    syncPlusSign(e.target);
    if (typeof userChange === 'function') userChange(e);
  };

  const userInput = options.input;
  options.input = (e) => {
    clearValidity(e.target);
    syncPlusSign(e.target);
    if (typeof userInput === 'function') userInput(e);
  };

  if (showPlus) {
    if (typeof globalThis.requestAnimationFrame === 'function') {
      globalThis.requestAnimationFrame(() => syncPlusSign(elem));
    } else {
      Promise.resolve().then(() => syncPlusSign(elem));
    }
  }

  return [elem, options];
};
